import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { apiGet, apiPost, API, getToken } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { ProgressBar } from "@/components/ProgressBar";
import { classAccent } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ChevronLeft, Download, StickyNote } from "lucide-react";

export default function StudentDetail({ mode }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [activeClass, setActiveClass] = useState(0);
  const [loading, setLoading] = useState(true);

  const base = mode === "mentor" ? "/mentor/students" : "/manage/students";
  const backTo = mode === "mentor" ? "/mentor" : "/admin";

  const load = useCallback(async () => {
    try {
      const s = await apiGet(`${base}/${id}`);
      setStudent(s);
      if (mode === "mentor") {
        const n = await apiGet(`/mentor/students/${id}/notes`);
        setNotes(n);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [base, id, mode]);

  useEffect(() => { load(); }, [load]);

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      await apiPost("/mentor/notes", { student_id: id, note_text: noteText.trim() });
      setNoteText("");
      const n = await apiGet(`/mentor/students/${id}/notes`);
      setNotes(n);
      toast.success("Note saved");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const exportCsv = async () => {
    try {
      const res = await fetch(`${API}/manage/students/${id}/export.csv`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const text = await res.text();
      const blob = new Blob([text], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${student?.name || "student"}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const nav = mode === "mentor" ? [{ to: "/mentor", label: "My Students" }] : [{ to: "/admin", label: "Admin" }];

  if (loading)
    return <AppShell nav={nav}><div className="h-96 rounded-3xl border-2 border-ink bg-white/60 animate-pulse" /></AppShell>;
  if (!student) return null;

  const cls = student.classes?.[activeClass];

  return (
    <AppShell nav={nav}>
      <button onClick={() => navigate(backTo)} className="flex items-center gap-1 text-sm font-bold mb-5 press" data-testid="back-button">
        <ChevronLeft size={16} /> Back
      </button>

      <div className="rounded-3xl border-2 border-ink bg-white shadow-pop p-6 mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold">{student.name}</h1>
          <p className="text-muted-foreground">{student.email}</p>
          {student.organization_name && (
            <span className="inline-block mt-2 text-xs font-bold px-3 py-1 rounded-full border-2 border-ink" style={{ background: "#B8FF3B" }}>
              {student.organization_name}
            </span>
          )}
        </div>
        {mode === "manage" && (
          <Button onClick={exportCsv} data-testid="export-csv" className="rounded-full border-2 border-ink press font-bold" style={{ background: "#31B8FF", color: "#0F1116" }}>
            <Download size={15} className="mr-1" /> Export CSV
          </Button>
        )}
      </div>

      {/* Class tabs */}
      <div className="flex flex-wrap gap-2 mb-5">
        {(student.classes || []).map((c, i) => (
          <button
            key={c.id}
            onClick={() => setActiveClass(i)}
            data-testid={`class-tab-${c.slug}`}
            className={`px-4 py-2 rounded-full border-2 border-ink text-sm font-bold press ${activeClass === i ? "shadow-pop-sm" : "bg-white"}`}
            style={activeClass === i ? { background: classAccent(c.slug, i, c.color).bg, color: classAccent(c.slug, i, c.color).fg } : {}}
          >
            {c.title} · {c.progress}%
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-5" data-testid="answers-panel">
          {!cls || cls.weeks.length === 0 ? (
            <div className="rounded-3xl border-2 border-ink bg-white p-8 text-center text-muted-foreground">
              No content / answers for this class.
            </div>
          ) : (
            cls.weeks.map((w) => (
              <div key={w.id} className="rounded-3xl border-2 border-ink bg-white overflow-hidden">
                <div className="px-5 py-3 border-b-2 border-ink" style={{ background: "#FFFDF5" }}>
                  <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Week {w.week_number}</span>
                  <div className="font-bold">{w.title}</div>
                </div>
                <div className="p-5 space-y-5">
                  {w.days.map((d) => (
                    <div key={d.id}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="h-6 w-6 grid place-items-center rounded-full border-2 border-ink text-xs font-bold" style={{ background: d.completed ? "#B8FF3B" : "#fff" }}>
                          {d.completed ? "✓" : d.day_number}
                        </span>
                        <span className="font-bold text-sm">{d.title}</span>
                      </div>
                      <div className="pl-8 space-y-3">
                        {d.questions.map((q) => (
                          <div key={q.id}>
                            <div className="text-sm font-semibold">{q.text}</div>
                            <div className={`text-sm font-reading mt-0.5 ${q.answer ? "" : "text-muted-foreground italic"}`} data-testid={`answer-view-${q.id}`}>
                              {q.answer || "No answer yet"}
                            </div>
                          </div>
                        ))}
                        {d.questions.length === 0 && <p className="text-xs text-muted-foreground">No questions.</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Notes (mentor only) */}
        {mode === "mentor" && (
          <div>
            <div className="rounded-3xl border-2 border-ink bg-white shadow-pop p-5 sticky top-24">
              <div className="flex items-center gap-2 mb-3 font-bold"><StickyNote size={16} /> Private notes</div>
              <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={3} placeholder="Add a private note…" className="border-2 border-ink rounded-xl resize-none mb-2" data-testid="note-input" />
              <Button onClick={addNote} className="w-full rounded-full border-2 border-ink press font-bold" data-testid="add-note">Save note</Button>
              <div className="mt-4 space-y-3 max-h-80 overflow-auto">
                {notes.map((n) => (
                  <div key={n.id} className="rounded-xl border-2 border-ink p-3" style={{ background: "#FFFDF5" }}>
                    <p className="text-sm font-reading">{n.note_text}</p>
                    <p className="text-[11px] text-muted-foreground mt-1">{new Date(n.created_at).toLocaleDateString()}</p>
                  </div>
                ))}
                {notes.length === 0 && <p className="text-sm text-muted-foreground">No notes yet.</p>}
              </div>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
